import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "components/card";
import { MdArrowBack, MdError, MdInventory, MdLocationOn, MdUpdate, MdViewList, MdViewModule, MdEdit, MdClose, MdSave } from "react-icons/md";
import toast from "react-hot-toast";
import axios from "axios";

// Add a loading component
const Loader = () => (
  <div className="flex h-[300px] sm:h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-16 w-16 sm:h-24 sm:w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-16 w-16 sm:h-24 sm:w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-3 w-3 sm:h-4 sm:w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-6 sm:mt-8 text-center">
        <p className="text-base sm:text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-xs sm:text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const FinishZoneDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { zoneId } = useParams();
  
  const [zone, setZone] = useState(location.state?.zone || null);
  const [loading, setLoading] = useState(!location.state?.zone);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('product'); // 'product' or 'rack'
  const [showEditModal, setShowEditModal] = useState(false);
  const [batchToEdit, setBatchToEdit] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!zone && zoneId) {
      fetchZoneDetails();
    }
  }, [zone, zoneId]);

  const fetchZoneDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
      
      // Find all products in the specified lane
      const zoneProducts = response.data.data.reduce((acc, product) => {
        const zoneBatches = product.batch_details
          .filter(batch => {
            // Check if the batch belongs to the specified lane
            if (batch.lane) {
              // For new data with lane field, match lanes like "Lane 1", "Lane 1A", etc.
              // Match exact "Lane X" or "Lane X" followed by one or more letters (A, B, etc.)
              // This prevents matching "Lane 10" when looking for "Lane 1"
              const laneMatch = new RegExp(`^Lane ${zoneId}([A-Z]+)?$`);
              return laneMatch.test(batch.lane);
            } else {
              // For old data, check zone number directly
              return batch.zone === parseInt(zoneId);
            }
          })
          .filter(batch => parseInt(batch.batch_actual_stock) > 0); // Only include batches with non-zero quantity
        
        if (zoneBatches.length > 0) {
          acc.push({
            name: product.product_type,
            actual_stock: parseInt(product.actual_stock),
            batches: zoneBatches.map(batch => {
              const processedBatch = {
                batchId: batch.batch_id,
                quantity: parseInt(batch.batch_actual_stock),
                rack: batch.rack,
                slot: batch.slot,
                lane: batch.lane || `Lane ${batch.zone}`,
                status: batch.status,
                age: batch.age,
                exported: parseInt(batch.batch_exported),
                rework: parseInt(batch.batch_rework),
                date: new Date(batch.date),
                julian_date: batch.julian_date || 'N/A',
                created_at: new Date(batch.created_at)
              };
              return processedBatch;
            })
          });
        }
        return acc;
      }, []);

      const totalQuantity = zoneProducts.reduce((total, product) => 
        total + product.batches.reduce((sum, batch) => sum + batch.quantity, 0), 0
      );

      const allDates = zoneProducts.flatMap(product => 
        product.batches.map(batch => batch.created_at).filter(date => date)
      );
      const latestUpdate = allDates.length > 0 
        ? new Date(Math.max(...allDates))
        : new Date();

      setZone({
        id: zoneId,
        name: `Lane ${zoneId}`,
        totalProducts: zoneProducts.length,
        totalQuantity,
        lastUpdated: latestUpdate,
        products: zoneProducts
      });
      
      setLoading(false);
    } catch (error) {
      // console.error('Error fetching zone details:', error);
      setError('Failed to fetch zone details');
      setLoading(false);
      toast.error('Failed to fetch zone details');
    }
  };

  const renderProductView = () => (
    <div className="space-y-4 sm:space-y-6">
      {zone.products.map((product) => (
        <div
          key={product.name}
          className="bg-gray-50 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-colors"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <MdInventory className="text-blue-600 sm:w-6 sm:h-6" size={20} />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 break-words">{product.name}</h3>
            </div>
            <div className="text-left sm:text-right">
              <span className="text-base sm:text-lg font-semibold text-blue-600">
                {product.batches.reduce((total, batch) => total + batch.quantity, 0)} boxes total
              </span>
            </div>
          </div>
          
          <div className="sm:ml-8 lg:ml-12 space-y-2 sm:space-y-3">
            {product.batches.map((batch) => {
              // console.log('Rendering batch:', batch.batchId, 'Julian date:', batch.julian_date); // Debug render
              return (
              <div key={batch.batchId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg p-3 gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-xs sm:text-sm font-medium">
                      {batch.batchId}
                    </span>
                    <span className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium ${
                      batch.status === 'Good Condition' ? 'bg-green-100 text-green-700' :
                      batch.status === 'Needs Rework' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {batch.status}
                    </span>
                    <span className="px-2 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-xs sm:text-sm font-medium">
                      JD: {batch.julian_date}
                    </span>
                  </div>
                  <div className="flex items-center text-xs sm:text-sm text-gray-500">
                    <MdLocationOn size={14} className="sm:w-4 sm:h-4 mr-1" />
                    <span>{batch.lane || `Lane ${zoneId}`}, Slot {batch.slot || batch.rack}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <span className="text-sm font-semibold text-gray-700">{batch.quantity} boxes</span>
                  <button
                    onClick={() => openEditModal(batch)}
                    className="rounded-lg p-1.5 transition-all duration-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                    title="Edit quantity"
                  >
                    <MdEdit className="h-4 w-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  // Edit modal functions
  const openEditModal = (batch) => {
    setBatchToEdit(batch);
    setEditQuantity(batch.quantity.toString());
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setBatchToEdit(null);
    setEditQuantity('');
    setIsEditing(false);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!batchToEdit) return;

    const newQuantity = parseInt(editQuantity);
    if (newQuantity > 72) {
      toast.error('Quantity cannot exceed 72 boxes');
      return;
    }

    setIsEditing(true);
    try {
      // Handle date properly - it might be a string or Date object
      let dateString;
      if (batchToEdit.date instanceof Date) {
        dateString = batchToEdit.date.toISOString().split('T')[0];
      } else if (typeof batchToEdit.date === 'string') {
        dateString = new Date(batchToEdit.date).toISOString().split('T')[0];
      } else {
        // Fallback to today's date if date is undefined
        dateString = new Date().toISOString().split('T')[0];
      }

      const response = await axios.put(`https://usibras-web-backend.onrender.com/api/finished-products/${batchToEdit.batchId}`, {
        product_type: batchToEdit.productName,
        date: dateString,
        quantity: newQuantity,
        lane: batchToEdit.lane,
        slot: batchToEdit.slot || batchToEdit.rack
      });

      if (response.data && response.data.message === "Product updated successfully") {
        toast.success('Quantity updated successfully!');
        
        // Update the local state with the new quantity
        setZone(prevZone => ({
          ...prevZone,
          products: prevZone.products.map(product => ({
            ...product,
            batches: product.batches.map(batch => 
              batch.batchId === batchToEdit.batchId 
                ? { ...batch, quantity: newQuantity }
                : batch
            )
          }))
        }));
        
        closeEditModal();
      } else {
        toast.error('Failed to update quantity. Please try again.');
      }
    } catch (error) {
      // console.error('Error updating quantity:', error);
      toast.error('Failed to update quantity. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };

  const renderRackView = () => (
    <div className="space-y-4 sm:space-y-6">
      {Object.entries(zone.products.reduce((acc, product) => {
        product.batches.forEach(batch => {
          const slotKey = batch.slot || batch.rack;
          if (!acc[slotKey]) {
            acc[slotKey] = [];
          }
          acc[slotKey].push({
            ...batch,
            productName: product.name
          });
        });
        return acc;
      }, {})).sort(([slotA], [slotB]) => {
        // Extract numeric part from slot for proper sorting
        const numA = parseInt(slotA.replace(/\D/g, '')) || 0;
        const numB = parseInt(slotB.replace(/\D/g, '')) || 0;
        return numA - numB;
      }).map(([slot, batches]) => (
        <div
          key={slot}
          className="bg-gray-50 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-colors"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <MdLocationOn className="text-blue-600 sm:w-6 sm:h-6" size={20} />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Slot {slot}</h3>
            </div>
            <div className="text-left sm:text-right">
              <span className="text-base sm:text-lg font-semibold text-blue-600">
                {batches.reduce((total, batch) => total + batch.quantity, 0)} boxes total
              </span>
            </div>
          </div>
          
          <div className="sm:ml-8 lg:ml-12 space-y-2 sm:space-y-3">
            {batches.map((batch) => (
              <div key={batch.batchId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg p-3 gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-xs sm:text-sm font-medium">
                      {batch.productName}
                    </span>
                    <span className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-xs sm:text-sm font-medium">
                      {batch.batchId}
                    </span>
                    <span className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium ${
                      batch.status === 'Good Condition' ? 'bg-green-100 text-green-700' :
                      batch.status === 'Needs Rework' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {batch.status}
                    </span>
                    <span className="px-2 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-xs sm:text-sm font-medium">
                      JD: {batch.julian_date}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <span className="text-sm font-semibold text-gray-700">{batch.quantity} boxes</span>
                  <button
                    onClick={() => openEditModal(batch)}
                    className="rounded-lg p-1.5 transition-all duration-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                    title="Edit quantity"
                  >
                    <MdEdit className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="mt-5 h-full">
      {loading ? (
        <Loader />
      ) : error ? (
        <div className="flex h-[300px] sm:h-[400px] items-center justify-center">
          <div className="text-center px-4">
            <div className="mb-3 sm:mb-4 text-4xl sm:text-5xl text-red-500">
              <MdError className="mx-auto h-12 w-12 sm:h-16 sm:w-16" />
            </div>
            <p className="text-lg sm:text-xl font-semibold text-gray-700 dark:text-white">
              Oops! Something went wrong
            </p>
            <p className="mt-2 text-sm sm:text-base text-gray-500 dark:text-gray-400">{error}</p>
            <button
              onClick={() => navigate('/admin/finished-products/zones')}
              className="mt-4 sm:mt-6 rounded-xl bg-brand-500 px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg"
            >
              Go Back to Lanes
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 sm:mt-5">
          <Card extra="w-full p-3 sm:p-4 lg:p-6">
            {/* Header */}
            <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:gap-4 border-b border-gray-200 pb-3 sm:pb-4 dark:border-navy-600">
              {/* Top row with back button and title */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => navigate('/admin/finished-products/zones')}
                    className="rounded-lg p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-navy-600"
                    aria-label="Go back to lanes"
                  >
                    <MdArrowBack className="h-5 w-5 sm:h-6 sm:w-6 text-navy-700 dark:text-white" />
                  </button>
                  <h2 className="text-xl sm:text-2xl font-bold text-navy-700 dark:text-white break-words">
                    {zone.name} Details
                  </h2>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => setViewMode('product')}
                    className={`flex items-center gap-1 sm:gap-2 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-sm transition-colors ${
                      viewMode === 'product' 
                        ? 'bg-brand-500 text-white' 
                        : 'hover:bg-gray-100 dark:hover:bg-navy-600 text-gray-600 dark:text-gray-300'
                    }`}
                    title="View b Product Type"
                  >
                    <MdViewList size={18} className="sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">By Product</span>
                  </button>
                  <button
                    onClick={() => setViewMode('rack')}
                    className={`flex items-center gap-1 sm:gap-2 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-sm transition-colors ${
                      viewMode === 'rack' 
                        ? 'bg-brand-500 text-white' 
                        : 'hover:bg-gray-100 dark:hover:bg-navy-600 text-gray-600 dark:text-gray-300'
                    }`}
                    title="View by Slot"
                  >
                    <MdViewModule size={18} className="sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">By Slot</span>
                  </button>
                </div>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1 sm:gap-2 rounded-lg bg-gray-50 px-2 sm:px-3 py-1.5 sm:py-2 dark:bg-navy-700">
                  <MdInventory className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>{zone.totalProducts} product types</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 rounded-lg bg-gray-50 px-2 sm:px-3 py-1.5 sm:py-2 dark:bg-navy-700">
                  <MdLocationOn className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>{zone.totalQuantity} boxes total</span>
                </div>
                {/* Latest update display commented out
                <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-navy-700">
                  <MdUpdate className="h-4 w-4" />
                  <span>Last updated: {new Date(zone.lastUpdated).toLocaleString()}</span>
                </div>
                */}
              </div>
            </div>

            {/* Content */}
            {viewMode === 'product' ? renderProductView() : renderRackView()}
          </Card>
        </div>
      )}

      {/* Edit Quantity Modal */}
      {showEditModal && batchToEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl dark:bg-navy-800">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                    <MdEdit className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Edit Quantity
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Update batch quantity (max: 72 boxes)
                  </p>
                </div>
              </div>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <MdClose className="h-6 w-6" />
              </button>
            </div>

            {/* Current Batch Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg dark:bg-navy-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Batch Information</h4>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Batch ID:</span>
                  <p className="text-gray-900 dark:text-white">{batchToEdit.batchId}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Product:</span>
                  <p className="text-gray-900 dark:text-white">{batchToEdit.productName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Location:</span>
                  <p className="text-gray-900 dark:text-white">
                    {batchToEdit.lane}, Slot {batchToEdit.slot || batchToEdit.rack}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Current Quantity:</span>
                  <p className="text-gray-900 dark:text-white">{batchToEdit.quantity} boxes</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Julian Date:</span>
                  <p className="text-gray-900 dark:text-white">{batchToEdit.julian_date}</p>
                </div>
              </div>
            </div>

            {/* Edit Form */}
            <form onSubmit={handleEditSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                  min="1"
                  max="72"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  placeholder="Enter new quantity (max: 72)"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Maximum allowed quantity: 72 boxes
                </p>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-navy-600">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={isEditing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 dark:bg-navy-700 dark:text-gray-300 dark:border-navy-600 dark:hover:bg-navy-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isEditing || !editQuantity || parseInt(editQuantity) > 72}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
                >
                  {isEditing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <MdSave className="h-4 w-4" />
                      <span>Update Quantity</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinishZoneDetails; 